import { connect } from 'react-redux';
import { upperFirst } from 'lodash';
import i18next from 'i18next';
import React, { useCallback, useMemo, useState } from 'react';

import { ListView, Button } from '@commons/utils/styledLibraryComponents';
import EmptyState from '@commons/EmptyState';

import { getClientInfo } from '@selectors/client';

import { getRoleRightsColumns } from '../../utils/getRoleRightsColumns';
import { useRoleModalFilters } from '../../utils/useRoleModalFilters';

import RoleModalFilterButton from '../RoleModalFilterButton';

import {
  Container,
  Header,
  HeaderTitle,
  CloseImage,
  ListViewContainer,
  Footer,
} from './styledComponents';

const EditRoleRights = (props) => {
  const {
    params: { role, closeModal, actions, handleSave },
    client: { hasCentralKitchens },
  } = props;

  const [columns] = useState(getRoleRightsColumns(hasCentralKitchens));

  // Source of truth for the whole selection, initialized from the initial rights.
  // Kept independent of the filter so selections hidden by the current filter are never lost.
  const [selectedIds, setSelectedIds] = useState(
    () => new Set(actions.filter(({ isRowSelected }) => isRowSelected).map(({ id }) => id)),
  );

  // Feed the filter with data carrying the up-to-date selection so switching filters
  // keeps the user's in-session changes both visible and persisted.
  const actionsWithSelection = useMemo(
    () =>
      actions.map((action) => ({
        ...action,
        isRowSelected: selectedIds.has(action.id),
      })),
    [actions, selectedIds],
  );

  const filtersState = useRoleModalFilters(actionsWithSelection, columns);
  const { filteredData, columnsFilterList } = filtersState;

  // The ListView only reports the selection of the currently visible (filtered) rows.
  // Reconcile it with the persisted set: rewrite only the visible rows, leave hidden ones untouched.
  const handleSelectionChange = useCallback(
    (visibleSelectedItems) => {
      const visibleIds = new Set(filteredData.map(({ id }) => id));
      const selectedVisibleIds = new Set(visibleSelectedItems.map(({ id }) => id));

      setSelectedIds((previousIds) => {
        const nextIds = new Set([...previousIds].filter((id) => !visibleIds.has(id)));

        selectedVisibleIds.forEach((id) => nextIds.add(id));

        const isUnchanged =
          nextIds.size === previousIds.size && [...nextIds].every((id) => previousIds.has(id));

        // Avoid a re-render loop: the ListView re-emits the selection whenever `data` changes.
        if (isUnchanged) {
          return previousIds;
        }

        return nextIds;
      });
    },
    [filteredData],
  );

  const handleSaveClick = useCallback(() => {
    const selectedActions = actions.filter(({ id }) => selectedIds.has(id));

    handleSave(selectedActions, role.id);
  }, [actions, selectedIds, handleSave, role.id]);

  return (
    <Container>
      <Header>
        <HeaderTitle>
          {i18next.t('BACKOFFICE.ROLES.PAGE_EDIT_RIGHTS', {
            role: upperFirst(role.name),
          })}
        </HeaderTitle>
        <CloseImage src="/images/inpulse/close-black-small.svg" onClick={() => closeModal()} />
      </Header>
      <ListViewContainer>
        <ListView
          columns={columns}
          data={filteredData}
          defaultOrderBy={'path'}
          padding={'24px 24px 0px 24px'}
          placeholderShape={i18next.t('GENERAL.SEARCH')}
          renderEmptyState={() => <EmptyState label={i18next.t('GENERAL.NO_RESULT')} />}
          renderFilterButton={() => (
            <RoleModalFilterButton
              columnsFilterList={columnsFilterList}
              filtersState={filtersState}
            />
          )}
          setSelectedItems={handleSelectionChange}
          disableFooter
          forceEnableSelection
          hideSearchbar
        />
      </ListViewContainer>
      <Footer>
        <Button
          color={'inpulse-outline'}
          handleClick={() => closeModal()}
          icon="/images/inpulse/close-black-small.svg"
          label={i18next.t('GENERAL.CANCEL')}
        />
        <Button
          buttonCustomStyle={{ marginLeft: 24 }}
          color={'inpulse-default'}
          handleClick={handleSaveClick}
          icon="/images/inpulse/save-white-small.svg"
          isDisabled={!selectedIds.size}
          label={i18next.t('GENERAL.SAVE')}
        />
      </Footer>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
});

export default connect(mapStateToProps)(EditRoleRights);
